interface IndexedDBTypes {
    putData(data: any): Promise<boolean>;
    getAllData(): Promise<[]>;
    getAllKeys(): Promise<IDBValidKey[]>;
    getDataById(id: string): Promise<any>;
    deleteDataById(id: string): Promise<boolean>;
}

class IndexedDBServiceBase implements IndexedDBTypes {
    private readonly objectStore: string;
    private readonly dbversion: number;
    private readonly dbname: string;
    private readonly keyPath: string;

    constructor(databaseConfig) {
        this.objectStore = databaseConfig.objectStore;
        this.dbversion = databaseConfig.database_version;
        this.dbname = databaseConfig.database;
        this.keyPath = databaseConfig.keyPath;
    }

    private openDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbname, this.dbversion);
            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBRequest).result;
                if (!db.objectStoreNames.contains(this.objectStore)) {
                    db.createObjectStore(this.objectStore, {
                        keyPath: this.keyPath,
                        autoIncrement: false
                    });
                }
            };

            request.onsuccess = event => {
                resolve((event.target as IDBRequest).result);
            };

            request.onerror = error => {
                console.error(error);
                reject("Error opening db");
            };
        });
    }

    public async putData(data) {
        const db = await this.openDatabase();
        return new Promise<boolean>(async (resolve, reject) => {
            const transaction = db.transaction(this.objectStore, "readwrite");
            const store = transaction.objectStore(this.objectStore);
            const request = store.put(data);

            request.onsuccess = () => {
                resolve(true);
            };

            request.onerror = error => {
                reject("Error setting data: " + error);
            };
        });
    }

    public async getAllData() {
        const db = await this.openDatabase();
        return new Promise<[]>(async (resolve, reject) => {
            const transaction = db.transaction(this.objectStore, "readonly");
            const store = transaction.objectStore(this.objectStore);
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = error => {
                reject("Error fetching data: " + error);
            };
        });
    }

    public async getAllKeys() {
        const db = await this.openDatabase();
        return new Promise<IDBValidKey[]>(async (resolve, reject) => {
            const transaction = db.transaction(this.objectStore, "readonly");
            const objectStore = transaction.objectStore(this.objectStore);

            const getAllKeysRequest = objectStore.getAllKeys();

            getAllKeysRequest.onsuccess = () => {
                const keys = getAllKeysRequest.result;
                resolve(keys);
            };

            getAllKeysRequest.onerror = error => {
                reject("Error fetching object store keys: " + error);
            };
        });
    }

    public async getDataById(id: string) {
        const db = await this.openDatabase();
        return new Promise<any>(async (resolve, reject) => {
            const transaction = db.transaction(this.objectStore, "readonly");
            const store = transaction.objectStore(this.objectStore);
            const request = store.get(id);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = error => {
                reject("Error fetching data by ID: " + error);
            };
        });
    }

    public async deleteDataById(id: string) {
        const db = await this.openDatabase();
        return new Promise<boolean>(async (resolve, reject) => {
            const transaction = db.transaction(this.objectStore, "readwrite");
            const store = transaction.objectStore(this.objectStore);
            const request = store.delete(id);

            request.onsuccess = () => {
                resolve(true);
            };

            request.onerror = error => {
                reject("Error deleting data: " + error);
            };
        });
    }
}

export default IndexedDBServiceBase;
